import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, Loader2, RotateCcw, Sparkles, Save, AlertTriangle, CheckCircle2, Wand2, Type } from "lucide-react";
import { toast } from "sonner";
import { contrastReport, isHex, normHex, loadGoogleFont, ROLE_LABELS, ROLE_HELP, isLight, pickText } from "./stylerUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["primary", "accent", "secondary", "neutral"];

function groupFonts(fonts) {
  const g = {};
  fonts.forEach((f) => { (g[f.category] = g[f.category] || []).push(f.family); });
  return g;
}

const VIDEO_BG = "linear-gradient(160deg, #6d7f6a 0%, #3f4f3f 50%, #24302a 100%)";
const alphaHex = (pct) => Math.round((pct / 100) * 255).toString(16).padStart(2, "0").toUpperCase();
// Solid card at the chosen opacity over a video-like frame — mirrors what the generator emits
const cardBg = (hex, pct) => (pct >= 100 || !isHex(hex) ? hex : `linear-gradient(${hex}${alphaHex(pct)}, ${hex}${alphaHex(pct)}), ${VIDEO_BG}`);

export default function StylerBrandPanel({ brand, setBrand, fonts, onGenerate, generating, autoFix, setAutoFix, cardOpacity = 80, setCardOpacity, onSaved }) {
  const [analysing, setAnalysing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const grouped = groupFonts(fonts);
  const report = contrastReport(brand);
  // Same resolution the generator applies — the preview shows what will actually be produced
  const onPrimaryHero = pickText(brand.primary, "hero", brand, brand.neutral).color;
  const onPrimarySupport = pickText(brand.primary, "support", brand, brand.accent).color;
  const onAccentHero = pickText(brand.accent, "hero", brand, brand.primary).color;
  const onAccentSupport = pickText(brand.accent, "support", brand, brand.primary).color;

  useEffect(() => { loadGoogleFont(brand.hero_font); loadGoogleFont(brand.support_font); }, [brand.hero_font, brand.support_font]);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Please use a PNG, JPG or WEBP logo");
    setAnalysing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/template-styler/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setBrand((b) => ({
        ...b,
        name: b.name || d.brand_name || "",
        logo_url: d.logo_url,
        logo_preview: d.logo_data_url || d.logo_url,
        palette: d.palette,
        primary: d.suggested.primary, accent: d.suggested.accent, secondary: d.suggested.secondary, neutral: d.suggested.neutral,
        hero_font: d.typography.hero_font, support_font: d.typography.support_font,
        detected: { ...d.suggested, hero_font: d.typography.hero_font, support_font: d.typography.support_font },
        typography: d.typography,
        id: null,
      }));
      toast.success("Logo analysed — check the palette and fonts, then generate");
    } catch (e) {
      const dd = e?.response?.data?.detail;
      toast.error(typeof dd === "string" ? dd : "Logo analysis failed");
    } finally {
      setAnalysing(false);
    }
  };

  const setRole = (role, hex) => setBrand((b) => ({ ...b, [role]: normHex(hex) }));
  const resetRole = (role) => brand.detected?.[role] && setRole(role, brand.detected[role]);
  const rolesOf = (hex) => ROLES.filter((r) => (brand[r] || "").toUpperCase() === hex.toUpperCase());

  const saveBrand = async () => {
    if (!brand.name.trim()) return toast.error("Give the brand a name first");
    setSaving(true);
    try {
      const body = {
        name: brand.name.trim(), logo_url: brand.logo_url, primary: brand.primary, accent: brand.accent,
        secondary: brand.secondary || null, neutral: brand.neutral, hero_font: brand.hero_font, support_font: brand.support_font, palette: brand.palette,
      };
      const r = brand.id
        ? await axios.put(`${API}/template-styler/brands/${brand.id}`, body)
        : await axios.post(`${API}/template-styler/brands`, body);
      onSaved(r.data);
      toast.success(`${r.data.name} saved`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save brand");
    } finally {
      setSaving(false);
    }
  };

  const canGenerate = ROLES.every((r) => r === "secondary" || isHex(brand[r])) && brand.hero_font && brand.support_font;

  return (
    <section className="ts-panel" data-testid="styler-brand-panel">
      {/* ── Step 1: logo ── */}
      <div className="ts-row">
        <div
          className={`ts-drop ${dragOver ? "over" : ""} ${brand.logo_preview ? "has-logo" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="styler-drop-zone"
        >
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="styler-logo-input" />
          {analysing ? (
            <div className="ts-drop-inner"><Loader2 className="animate-spin" size={26} /><strong>Analysing brand…</strong><span>Reading colours & typography</span></div>
          ) : brand.logo_preview ? (
            <div className="ts-logo-preview"><img src={brand.logo_preview} alt="Brand logo" data-testid="styler-logo-preview" /><span className="ts-drop-replace">Drop a new logo to replace</span></div>
          ) : (
            <div className="ts-drop-inner"><UploadCloud size={30} /><strong>Upload brand logo</strong><span>Drag & drop PNG / JPG / WEBP — transparent PNG works best</span></div>
          )}
        </div>

        <div className="ts-brand-meta">
          <label className="ts-label">Brand name</label>
          <input className="ts-input ts-input-lg" value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} placeholder="e.g. Hamptons" data-testid="styler-brand-name" />
          {brand.typography && (
            <div className="ts-typo-note" data-testid="styler-typo-note">
              <Wand2 size={13} />
              <span><strong>{brand.typography.typography_category}</strong> · {brand.typography.rationale || "Suggested from the logo"}</span>
            </div>
          )}
          {brand.palette?.length > 0 && (
            <>
              <label className="ts-label">Detected colours — tap a role to assign</label>
              <div className="ts-extracted" data-testid="styler-extracted-palette">
                {brand.palette.map((p) => {
                  const assigned = rolesOf(p.hex);
                  return (
                    <div key={p.hex} className={`ts-ex-swatch ${assigned.length ? "assigned" : ""}`} data-testid={`styler-extracted-${p.hex.slice(1)}`}>
                      <div className="ts-ex-color" style={{ background: p.hex }} title={`${p.name} · ${Math.round(p.share * 100)}%`}>
                        {p.is_background && <span className="ts-ex-bg">bg</span>}
                      </div>
                      <div className="ts-ex-meta">
                        <span className="ts-ex-hex">{p.hex}</span>
                        <span className="ts-ex-name">{p.name}{p.share ? ` · ${Math.round(p.share * 100)}%` : ""}</span>
                      </div>
                      <div className="ts-ex-roles">
                        {ROLES.map((r) => (
                          <button key={r} className={`ts-role-pill ${assigned.includes(r) ? "on" : ""}`} onClick={() => setRole(r, p.hex)} data-testid={`styler-assign-${r}-${p.hex.slice(1)}`}>
                            {ROLE_LABELS[r][0]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Step 2: palette ── */}
      <div className="ts-section-head"><span>Brand palette</span><small>Every colour is editable — the masters map roles, not pixels</small></div>
      <div className="ts-roles" data-testid="styler-role-cards">
        {ROLES.map((r) => {
          const val = brand[r] || "";
          const detected = brand.detected?.[r];
          return (
            <div key={r} className="ts-role-card" data-testid={`styler-role-${r}`}>
              <label className="ts-role-swatch" style={{ background: isHex(val) ? val : "#222", color: isLight(val) ? "#1c2a1c" : "#fff" }}>
                <input type="color" value={isHex(val) ? val : "#000000"} onChange={(e) => setRole(r, e.target.value)} data-testid={`styler-color-${r}`} />
                <span className="ts-role-name">{ROLE_LABELS[r].toUpperCase()}</span>
                <span className="ts-role-hex">{val}</span>
              </label>
              <div className="ts-role-controls">
                <input className="ts-input ts-hex" value={val} onChange={(e) => setBrand({ ...brand, [r]: e.target.value.toUpperCase() })} onBlur={(e) => isHex(e.target.value) && setRole(r, e.target.value)} placeholder="#RRGGBB" data-testid={`styler-hex-${r}`} />
                <button className="ts-icon-btn" title="Reset to detected" disabled={!detected || detected.toUpperCase() === val.toUpperCase()} onClick={() => resetRole(r)} data-testid={`styler-reset-${r}`}><RotateCcw size={13} /></button>
              </div>
              <p className="ts-role-help">{ROLE_HELP[r]}</p>
              {!isHex(val) && <p className="ts-role-err">Enter a valid hex</p>}
            </div>
          );
        })}
      </div>

      {report.length > 0 && (
        <div className="ts-contrast" data-testid="styler-contrast">
          {report.map((c) => (
            <div key={c.label} className={`ts-contrast-row ${c.ok ? "ok" : "fixed"}`} data-testid={`styler-contrast-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="ts-contrast-sample" style={{ background: c.bg, color: c.picked }}>Aa</span>
              <span className="ts-contrast-label">{c.label}</span>
              <span className="ts-contrast-ratio">{c.ok ? `${c.ratio}:1` : `${c.ratio}:1 → ${c.pickedRatio}:1`}</span>
              {c.ok ? <CheckCircle2 size={14} /> : <span className="ts-contrast-fix"><AlertTriangle size={13} /> Uses {ROLE_LABELS[c.pickedRole] || c.picked} instead</span>}
            </div>
          ))}
          {report.some((c) => !c.ok) && (
            <p className="ts-contrast-note">Text that can't be read on its card is swapped for another brand colour during generation — supporting copy leans on your Secondary, display text on your Neutral — and listed under Show Changes. Your brand colours themselves are never changed.</p>
          )}
        </div>
      )}

      {/* ── Step 3: typography ── */}
      <div className="ts-section-head"><span>Typography</span><small>Creatomate-safe Google fonts only</small></div>
      <div className="ts-typo">
        <div className="ts-typo-selects">
          {[["hero_font", "Hero / Display font"], ["support_font", "Supporting font"]].map(([key, label]) => (
            <div key={key} className="ts-field">
              <label className="ts-label"><Type size={12} /> {label}</label>
              <select className="ts-input ts-select" value={brand[key]} onChange={(e) => setBrand({ ...brand, [key]: e.target.value })} data-testid={`styler-${key.replace("_", "-")}`}>
                {Object.entries(grouped).map(([cat, fams]) => (
                  <optgroup key={cat} label={cat.toUpperCase()}>
                    {fams.map((f) => <option key={f} value={f}>{f}</option>)}
                  </optgroup>
                ))}
              </select>
              {brand.detected?.[key] && brand.detected[key] !== brand[key] && (
                <button className="ts-link" onClick={() => setBrand({ ...brand, [key]: brand.detected[key] })}>Reset to {brand.detected[key]}</button>
              )}
            </div>
          ))}
        </div>
        <div className="ts-typo-preview" data-testid="styler-typo-preview">
          <div className="ts-typo-block">
            <span className="ts-eyebrow" style={{ fontFamily: brand.support_font, color: brand.accent }}>NEW PRICE</span>
            <span className="ts-display" style={{ fontFamily: brand.hero_font, color: brand.primary }}>£800,000</span>
          </div>
          <div className="ts-typo-block">
            <span className="ts-display" style={{ fontFamily: brand.hero_font, color: brand.primary }}>FERN COTTAGE</span>
            <span className="ts-eyebrow" style={{ fontFamily: brand.support_font, color: brand.accent }}>FARLEY HILL</span>
          </div>
        </div>
      </div>

      {/* ── Step 4: brand preview ── */}
      <div className="ts-section-head"><span>Brand preview</span><small>How the cards will read at {cardOpacity}% over the video — not a render</small></div>
      <div className="ts-frames" data-testid="styler-brand-preview">
        <div className="ts-frame" style={{ background: cardBg(brand.primary, cardOpacity) }} data-testid="styler-preview-card-primary">
          <span className="ts-f-eyebrow" style={{ fontFamily: brand.support_font, color: onPrimarySupport }} data-testid="styler-preview-eyebrow">PRICE REDUCTION</span>
          <span className="ts-f-hero" style={{ fontFamily: brand.hero_font, color: onPrimaryHero }}>£800,000</span>
          <span className="ts-f-small" style={{ fontFamily: brand.support_font, color: onPrimarySupport }}>NOW £50,000 LESS</span>
        </div>
        <div className="ts-frame ts-frame-video">
          <div className="ts-f-panel" style={{ background: `${brand.primary}D1` }}>
            <span className="ts-f-hero" style={{ fontFamily: brand.hero_font, color: onPrimaryHero }}>FERN<br />COTTAGE</span>
            <span className="ts-f-small" style={{ fontFamily: brand.support_font, color: onPrimarySupport }}>FARLEY HILL</span>
          </div>
        </div>
        <div className="ts-frame" style={{ background: cardBg(brand.accent, cardOpacity) }} data-testid="styler-preview-card-accent">
          <span className="ts-f-small" style={{ fontFamily: brand.support_font, color: onAccentSupport }} data-testid="styler-preview-accent-eyebrow">COULD THIS BE</span>
          <span className="ts-f-hero" style={{ fontFamily: brand.hero_font, color: onAccentHero }} data-testid="styler-preview-accent-hero">THE ONE?</span>
        </div>
        <div className="ts-frame" style={{ background: cardBg(brand.primary, cardOpacity) }} data-testid="styler-preview-card-cta">
          <span className="ts-f-hero" style={{ fontFamily: brand.hero_font, color: onPrimaryHero }}>WANT TO<br />SEE MORE?</span>
          <span className="ts-f-small" style={{ fontFamily: brand.support_font, color: onPrimarySupport }}>BOOK YOUR VIEWING</span>
        </div>
        <div className="ts-frame ts-frame-logo" style={{ background: brand.primary }} data-testid="styler-preview-card-logo">
          {brand.logo_preview ? <img src={brand.logo_preview} alt="" className="ts-f-logo" /> : <span className="ts-f-small" style={{ fontFamily: brand.support_font, color: onPrimarySupport }}>LOGO</span>}
          <span className="ts-f-tag">100% · final card</span>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="ts-actions">
        <div className="ts-actions-opts">
          <label className="ts-check"><input type="checkbox" checked={autoFix} onChange={(e) => setAutoFix(e.target.checked)} data-testid="styler-autofix" /> Auto-fix low-contrast text (always shown in changes)</label>
          <label className="ts-check ts-opacity">Colour card opacity
            <select className="ts-input ts-select" value={cardOpacity} onChange={(e) => setCardOpacity?.(Number(e.target.value))} data-testid="styler-card-opacity">
              {[100, 90, 80, 70, 60].map((v) => <option key={v} value={v}>{v}%{v === 80 ? " (default)" : ""}</option>)}
            </select>
            <small>video shows through · logo card stays solid</small>
          </label>
        </div>
        <div className="ts-actions-btns">
          <button className="ts-btn ghost" onClick={saveBrand} disabled={saving || !canGenerate} data-testid="styler-save-brand">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} {brand.id ? "Update brand" : "Save brand"}
          </button>
          <button className="ts-btn primary" onClick={onGenerate} disabled={generating || !canGenerate} data-testid="styler-generate">
            {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} {generating ? "Restyling…" : "Generate templates"}
          </button>
        </div>
      </div>
    </section>
  );
}
